import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { CMSSection } from '@/cms/CMSSection';
import { Heading } from '@/components/Cms/Heading/Heading';
import { Container } from '@/components/Container';
import { Loader } from '@/components/Loader';
import { Typography } from '@/components/Typography';
import { VideoPlayer } from '@/components/Video';
import type {
  ContentfulCardFragment,
  ContentfulPlaceholderModelStoryFragment,
} from '@/graphql/contentful/api';
import type { ProductMediaFragment } from '@/graphql/saleor/api';
import { TEXT_MAX_LENGTH } from '@/lib/const';
import { generateDataTestId } from '@/lib/tests';
import type { Maybe } from '@/lib/types';
import { commonMessages } from '@/messages/common';
import { getBreakpointSize, theme } from '@/theme';

import { ReactComponent as PlayCircleSvg } from '@/assets/icons/play-circle.svg';

import * as S from './ModelStory.styles';
export interface ModelStoryOps {
  alt?: string;
  children: Maybe<ReactNode>;
  images: Maybe<ProductMediaFragment[]>;
}
export type ModelStoryProps = ContentfulPlaceholderModelStoryFragment &
  ModelStoryOps;
export const ModelStory = ({
  alt,
  children,
  images,
  media,
  mediaAlignment,
  storyBackgroundColor,
  storyHeading,
  type,
}: ModelStoryProps) => {
  const isServer = typeof window === 'undefined'; // Check if running on the server
  const defaultWindowWidth = isServer ? 0 : window.innerWidth;
  const [windowWidth, setWindowWidth] = useState(defaultWindowWidth);
  const [isVertical, setIsVertical] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { formatMessage } = useIntl();
  useEffect(() => {
    if (windowWidth < getBreakpointSize('md')) {
      setIsVertical(true);
    } else {
      setIsVertical(false);
    }
  }, [windowWidth]);
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const loading = !children;
  const mediaReversed = mediaAlignment === 'right';
  // The second image should be used in the model story.
  const image = images?.[1] || images?.[0];
  const defaultHeading: ContentfulCardFragment['placeholderHeading'] = {
    sys: {
      id: '',
    },
    textAlignment: isVertical ? 'center' : 'left',
    text: 'The Story',
  };
  const headingProps = storyHeading
    ? {
        ...storyHeading,
        text: storyHeading?.text || 'The Story',
        textAlignment: isVertical ? 'center' : 'left',
      }
    : defaultHeading;
  const openVideo = () => {
    setIsVideoOpen(true);
  };
  const onClose = () => {
    setIsVideoOpen(false);
  };
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };
  const textContent = children ? children.toString() : '';
  const shouldTruncate = isVertical && textContent.length > TEXT_MAX_LENGTH;
  const displayText =
    shouldTruncate && !isExpanded
      ? `${textContent.slice(0, TEXT_MAX_LENGTH)}...`
      : textContent;
  return (
    <CMSSection
      {...generateDataTestId('modelStory', 'section')}
      data-test-id='ModelStorySection'
    >
      <S.BackgroundWrapper storyBackgroundColor={storyBackgroundColor}>
        <Container>
          <S.Grid
            {...generateDataTestId('modelStory', 'wrapper')}
            $loading={loading}
            data-test-id='ModelStoryWrapper'
            mediaReversed={mediaReversed}
            storyBackgroundColor={storyBackgroundColor}
          >
            <S.StoryWrapper
              $loading={loading}
              data-test-id='ModelStoryContent'
              mediaReversed={mediaReversed}
              {...generateDataTestId('modelStory', 'content')}
            >
              <S.HeadingWrapper>
                <Heading
                  Heading={headingProps}
                  color={storyHeading?.mainColor ?? theme.colors.default}
                />
              </S.HeadingWrapper>
              {loading ? (
                <Loader data-test-id='ModelStoryLoader' height={350} />
              ) : (
                <>
                  <Typography as='span' data-testid='ModelStoryTypography'>
                    {displayText}
                  </Typography>
                  {shouldTruncate && (
                    <S.ReadMoreButton as='span' onClick={toggleExpand}>
                      {isExpanded
                        ? `[${formatMessage(commonMessages.readLess)}]`
                        : `[${formatMessage(commonMessages.readMore)}]`}
                    </S.ReadMoreButton>
                  )}
                </>
              )}
            </S.StoryWrapper>
            {!loading && image && (
              <S.MediaWrapper>
                <S.Picture
                  {...generateDataTestId('modelStory', 'image')}
                  alt={image.alt || alt}
                  bgColor={
                    type === 'eyewear'
                      ? theme.colors.transparent
                      : theme.colors.black
                  }
                  data-test-id='ModelStoryImage'
                  height={0}
                  loaderHeight={525}
                  loading='lazy'
                  sizes='50vw'
                  src={
                    (media?.contentType !== 'video/mp4' &&
                      media?.contentType !== 'video/webm' &&
                      media?.url) ||
                    image.url
                  }
                  width={0}
                />
                {(media?.contentType === 'video/mp4' ||
                  media?.contentType === 'video/webm') && (
                  <S.PlayCircleSvgWrapper
                    data-testid='ModelStoryPlayCircle'
                    onClick={openVideo}
                  >
                    <PlayCircleSvg />
                  </S.PlayCircleSvgWrapper>
                )}
              </S.MediaWrapper>
            )}
          </S.Grid>
        </Container>
      </S.BackgroundWrapper>
      {isVideoOpen && (
        <VideoPlayer
          mediaUrl={media?.url}
          onClose={onClose}
          title={media?.title}
        />
      )}
    </CMSSection>
  );
};
ModelStory.displayName = 'ModelStory';
